/* eslint-disable arrow-body-style */
import React, { useCallback, useMemo, useRef } from 'react'

import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { LoadingOverlay } from 'components/commonparts/index'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { useGridBase } from 'hooks/useGridBase'

import type { ColDef} from 'ag-grid-community'
import type { TransportSummaryData } from 'components/parts/OrderPlanTransportLot/type';

import 'ag-grid-enterprise'
import 'utils/mst.scss'

interface TransportSummaryProps {
  data: TransportSummaryData[] | undefined;
  isLoading?: boolean;
}
  // Transport Summary Grid Component
  // Displays vehicle count and weight data in a grid format grouped by time periods
export function TransportSummary({ data, isLoading }: TransportSummaryProps) {
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), [])
  const gridStyle = useMemo(() => ({ width: '100%', height: '100%' }), [])
  const gridRef = useRef<AgGridReact<TransportSummaryData>>(null)

  const {
    numberValueFormatter,
    formatDecimalValue,
    isNonInputValue,
    numberValueParser,
    onGridReady: baseOnGridReady,
    localeText,
  } = useGridBase({
    gridRef,
    isLoading,
  })

  const onGridReady = useCallback((event: any) => {
    baseOnGridReady(event)
    event.api.updateGridOptions({
      rowNumbers: false,
    })
  }, [baseOnGridReady])


  // Get date header display name (same as StockGrid format)
  const getheaderName = useCallback((dateStr: string) => {
    try {
      const parsedDate = new Date(dateStr)
      if (!Number.isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'M/d(E)', { locale: ja })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse date:', dateStr)
    }
    return dateStr
  }, [])

  // Group dates by time period (実績/当日/予定)
  const groupMap = useMemo(() => {
    if (!data || data.length === 0) return { '実績': [], '当日': [], '予定': [] }

    const map: { [key: string]: string[] } = { '実績': [], '当日': [], '予定': [] }
    data.forEach(item => {
      if (map[item.rectypeName]) {
        map[item.rectypeName].push(item.date)
      }
    })
    return map
  }, [data])

  // CSS class mapping for header groups
  const groupHeaderClassMap: Record<string, string> = {
    '実績': 'value-header-rec',     // Past records (gray)
    '当日': 'value-header-today',   // Today (orange)
    '予定': 'value-header-expected', // Future planned (blue)
  }


  // Transform data into grid rows (vehicle count and weight)
  const rows = useMemo(() => {
    if (!data || data.length === 0) return []

    return [
      {
        category: '台数',
        ...Object.fromEntries(data.map(item => [item.date, item.totalVehicles])),
        ...Object.fromEntries(data.map(item => [`${item.date}_isOrderDateToday`, item.isOrderDateToday]))
      },
      {
        category: '重さ',
        ...Object.fromEntries(data.map(item => [item.date, item.totalWeight])),
        ...Object.fromEntries(data.map(item => [`${item.date}_isOrderDateToday`, item.isOrderDateToday]))
      }
    ]
  }, [data])

  // Configure cell CSS classes
  const cellClass = useCallback((params: any) => {
    if (groupMap['当日']?.includes(params.colDef.field)) {
      return ['todays-order-cell', 'number-cell'];
    }
    // Check if this cell has isOrderDateToday = true for the corresponding date
    const dateField = params.colDef.field;
    const isOrderDateTodayField = `${dateField}_isOrderDateToday`;
    if (params.data[isOrderDateTodayField]) {
      return ['todays-order-cell', 'number-cell'];
    }
    return ['number-cell'];
  }, [groupMap])

  // Generate column definitions dynamically based on data
  const columnDefs = useMemo<ColDef[]>(() => [
    // First column for row labels
    {
      headerName: '',
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
      children: [
        {
          headerName: '項目',
          field: 'category',
          width: 90,
          pinned: 'left',
          suppressHeaderMenuButton: true,
          sortable: false,
          filter: false,
        },
      ]
    },
    // Dynamic date columns grouped by time period
    ...Object.entries(groupMap)
      .filter(([_, dateList]) => dateList.length > 0)
      .map(([label, dateList]) => ({
        headerName: label,
        headerClass: clsx(groupHeaderClassMap[label]),
        suppressHeaderMenuButton: true,
        sortable: false,
        filter: false,
        children: dateList.map(date => ({
          headerName: getheaderName(date),
          field: date,
          width: 95,
          type: 'rightAligned',
          headerClass: clsx(groupHeaderClassMap[label], {
            sunday: new Date(date).getDay() === 0, // Red text for Sundays
          }),
          cellClass,
          valueFormatter: (params: any) => {
            const rowData = params.data
            if (rowData && rowData.category === '台数') {
              return numberValueFormatter(params)
            }
            if (rowData && rowData.category === '重さ') {
              return formatDecimalValue(params, 3)
            }
            return params.value
          },
          valueParser: numberValueParser,
          editable: false,
          suppressHeaderMenuButton: true,
          sortable: false,
          filter: false,
        }))
      }))
  ], [groupMap, numberValueFormatter, formatDecimalValue, isNonInputValue, numberValueParser, cellClass, getheaderName])

  const defaultColDef = useMemo(() => ({
    resizable: true,
    suppressHeaderMenuButton: true,
    sortable: false,
    filter: false,
  }), [])

  // Show message when no data available
  if (!data || data.length === 0) {
    return (
      <div style={{ width: '100%', padding: '10px', textAlign: 'center' }}>
        車両予定データがありません
      </div>
    )
  }

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column', height: '160px' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            ref={gridRef}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            localeText={localeText}
            loadingOverlayComponent={LoadingOverlay}
            suppressAggFuncInHeader
            rowSelection={{ mode: 'singleRow', checkboxes: false, enableClickSelection: false }}
            suppressCellFocus
            suppressMenuHide
            suppressContextMenu
            rowHeight={36}
            suppressHorizontalScroll={false}
            suppressColumnVirtualisation={false}
            suppressRowVirtualisation={false}
            cellSelection
          />
        </div>
      </div>
    </div>
  )
}

// export default TransportSummary 